import { useEffect, useState } from "react";
import CustomBlockButton from "@/components/button/CustomBlockButton";
import * as St from './FdMobilePreview.styled';
import { useQueryParamsHook } from "@/hooks/useQueryParamsHook";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { useWindowResizeHook } from "@/hooks/useWindowResizeHook";
import { CustomDialog } from "@/components/dialog/v1/CustomDialog";
import { CustomRatioImage } from "@/components/image/CustomRatioImage";
import { getPreviewUrl } from '../../getPreviewUrl';
import { PostPreviewArticle } from '../FdPosts/FdPostPreview';
import { focusPostModule, usePostPreviewDraft } from '../FdPosts/postPreview';
import { SurveyPreviewArticle } from '../FdSurveys/FdSurveyPreview';
import { focusSurveyQuestion, useSurveyPreviewDraft } from '../FdSurveys/surveyQuestions';

export function FdMobilePreview(props) {
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const queryParamsHook = useQueryParamsHook();
    const params = queryParamsHook?.params;
    const isWritingPost = params?.tabType === 'POSTS' && !!params?.postId; // 이야기 쓰는 중엔 이야기 미리보기
    const isEditingSurvey = params?.tabType === 'SURVEYS' && !!params?.surveyId && params?.surveyView !== 'RESULTS'; // 설문 고치는 중엔 설문 미리보기

    // 열면 기록을 하나 쌓아서 휴대폰 뒤로가기가 페이지 대신 미리보기를 닫게
    useEffect(() => {
        if (!previewModalOpen) {
            return;
        }
        window.history.pushState({ mobilePreview: true }, '');
        const handlePopState = () => setPreviewModalOpen(false);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [previewModalOpen]);

    // 닫기 버튼: 바로 닫고, 쌓아 둔 기록은 뒤로 돌려 지운다
    const closePreview = () => {
        setPreviewModalOpen(false);
        if (window.history.state?.mobilePreview) {
            window.history.back();
        }
    }

    return (
        <>
            <button
                type='button'
                onClick={() => setPreviewModalOpen(true)}
                className="fixed bottom-5 left-1/2 z-[90] flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-gray-900 px-5 py-3 text-sm font-bold text-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.4)] transition active:scale-95"
            >
                <span aria-hidden="true">📱</span> 미리보기
            </button>

            {previewModalOpen && isWritingPost &&
                <MdPostPreview
                    onClose={closePreview}
                />
            }
            {previewModalOpen && isEditingSurvey &&
                <MdSurveyPreview
                    onClose={closePreview}
                />
            }
            {previewModalOpen && !isWritingPost && !isEditingSurvey &&
                <MdPreview
                    onClose={closePreview}
                />
            }
        </>
    );
}

function MdPreview({
    onClose
}) {
    const queryParamsHook = useQueryParamsHook();
    const lcId = queryParamsHook?.params?.lcId;
    const userPageContextValueHook = useUserPageContextValueHook();

    const url = getPreviewUrl(userPageContextValueHook.userPage?.domainName, lcId);

    return (
        <St.MdPreviewDeviceFrameContainer>
            <div className='floatingClose'>
                <CustomBlockButton
                    type='button'
                    onClick={onClose}
                >
                    <div className='iconFigure'>
                        <CustomRatioImage>
                            <CustomRatioImage.Image src={'/assets/icons/close_default_ffffff.svg'} />
                        </CustomRatioImage>
                    </div>
                </CustomBlockButton>
            </div>
            <iframe
                src={url}
                title='page_preview'
                httpEquiv="Content-Security-Policy"
                content="frame-src 'none';"
            ></iframe>
        </St.MdPreviewDeviceFrameContainer>
    );
}
/** 모바일 문서 미리보기 (전체 화면): 이야기·설문 공용 틀 */
function MdDocPreview({ title, hint, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-[#ececf0]" role="dialog" aria-modal="true" aria-label={title}>
            <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] bg-white/80 px-4 py-3 backdrop-blur">
                <div>
                    <p className="text-sm font-bold text-gray-900">{title}</p>
                    <p className="text-[11px] text-gray-500">{hint}</p>
                </div>
                <button type="button" onClick={onClose} className="rounded-full bg-gray-900 px-4 py-2 text-sm font-bold text-white">닫기</button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-4">
                {children}
            </div>
        </div>
    );
}

// 누른 부분을 고치러: 닫은 뒤 스크롤·포커스
const closeThen = (onClose, focus) => (key) => {
    onClose();
    setTimeout(() => focus(key));
}

/** 모바일 이야기 미리보기. 모듈을 누르면 닫고 그 모듈 편집기로 */
function MdPostPreview({ onClose }) {
    const draft = usePostPreviewDraft();
    return (
        <MdDocPreview title="이야기 미리보기" hint="누르면 그 부분을 고쳐요" onClose={onClose}>
            <PostPreviewArticle draft={draft} onSelectModule={closeThen(onClose, focusPostModule)} />
        </MdDocPreview>
    );
}

/** 모바일 설문 미리보기. 질문을 누르면 닫고 그 편집 카드로 */
function MdSurveyPreview({ onClose }) {
    const draft = useSurveyPreviewDraft();
    return (
        <MdDocPreview title="설문 미리보기" hint="누르면 그 질문을 고쳐요" onClose={onClose}>
            <SurveyPreviewArticle draft={draft} onSelectQuestion={closeThen(onClose, focusSurveyQuestion)} />
        </MdDocPreview>
    );
}
